import { useState, useEffect, useMemo } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import { ExclamationTriangleFill } from 'react-bootstrap-icons';
import { reasonOf } from '@/components/tools/clientSelectionUtils';
import { ToolPage } from '@/components/ui/ToolPage';
import {
  platformSettingsService,
  PLATFORM_QUOTA_INITIAL_VALUES,
  PlatformSettings as PlatformSettingsType,
} from '@/services/platformSettingsService';
import './PlatformSettings.css';

const PLATFORM_DEFAULT_MIN = PLATFORM_QUOTA_INITIAL_VALUES.schedulingMinIntervalMinutes;
const PLATFORM_MAX_MIN = 1440; // 1 day — reasonable upper bound

/** Mean minutes per month — matches lib/schedule-load.ts MINUTES_PER_MONTH. */
const MINUTES_PER_MONTH = 43_800;

/** What each stored setting is called on this page, for the notice that lists the ones never saved. */
const SETTING_NAME: Record<keyof typeof PLATFORM_QUOTA_INITIAL_VALUES, string> = {
  schedulingMinIntervalMinutes: 'shortest time between runs',
  maxRunsPerCompanyPerMonth: 'scheduled runs for each company',
  maxRunsPerUserPerMonth: 'scheduled runs for each person',
  maxTriggerRunsPerCompanyPerMonth: 'triggered runs for each company',
  maxTriggerRunsPerUserPerMonth: 'triggered runs for each person',
  maxConcurrentActiveSchedulesPerCompany: 'automations switched on for each company',
  maxConcurrentActiveSchedulesPerUser: 'automations switched on for each person',
  requireApprovalAboveUserCap: 'asking an admin past the limit',
};

export default function PlatformSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state — all numeric fields required; empty rejected on save.
  const [schedulingMinInterval, setSchedulingMinInterval] = useState<string>('');
  const [maxRunsPerCompanyPerMonth, setMaxRunsPerCompanyPerMonth] = useState<string>('');
  const [maxRunsPerUserPerMonth, setMaxRunsPerUserPerMonth] = useState<string>('');
  const [maxTriggerRunsPerCompanyPerMonth, setMaxTriggerRunsPerCompanyPerMonth] = useState<string>('');
  const [maxTriggerRunsPerUserPerMonth, setMaxTriggerRunsPerUserPerMonth] = useState<string>('');
  const [maxConcurrentActiveSchedulesPerCompany, setMaxConcurrentActiveSchedulesPerCompany] = useState<string>('');
  const [maxConcurrentActiveSchedulesPerUser, setMaxConcurrentActiveSchedulesPerUser] = useState<string>('');
  const [requireApprovalAboveUserCap, setRequireApprovalAboveUserCap] = useState<boolean>(
    PLATFORM_QUOTA_INITIAL_VALUES.requireApprovalAboveUserCap
  );
  // Names of any fields that were missing from the saved record and have
  // been pre-populated with PLATFORM_QUOTA_INITIAL_VALUES. Drives the
  // first-time-setup / incomplete-record warning banner.
  const [bootstrappedFields, setBootstrappedFields] = useState<string[]>([]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const settings = await platformSettingsService.get();
        const missing: string[] = [];
        const fillNum = <K extends keyof typeof PLATFORM_QUOTA_INITIAL_VALUES>(
          key: K,
          setter: (v: string) => void
        ): void => {
          const v = settings[key as keyof PlatformSettingsType];
          if (typeof v === 'number') {
            setter(String(v));
          } else {
            missing.push(String(key));
            setter(String(PLATFORM_QUOTA_INITIAL_VALUES[key]));
          }
        };
        fillNum('schedulingMinIntervalMinutes', setSchedulingMinInterval);
        fillNum('maxRunsPerCompanyPerMonth', setMaxRunsPerCompanyPerMonth);
        fillNum('maxRunsPerUserPerMonth', setMaxRunsPerUserPerMonth);
        fillNum('maxTriggerRunsPerCompanyPerMonth', setMaxTriggerRunsPerCompanyPerMonth);
        fillNum('maxTriggerRunsPerUserPerMonth', setMaxTriggerRunsPerUserPerMonth);
        fillNum('maxConcurrentActiveSchedulesPerCompany', setMaxConcurrentActiveSchedulesPerCompany);
        fillNum('maxConcurrentActiveSchedulesPerUser', setMaxConcurrentActiveSchedulesPerUser);
        if (typeof settings.requireApprovalAboveUserCap === 'boolean') {
          setRequireApprovalAboveUserCap(settings.requireApprovalAboveUserCap);
        } else {
          missing.push('requireApprovalAboveUserCap');
          setRequireApprovalAboveUserCap(PLATFORM_QUOTA_INITIAL_VALUES.requireApprovalAboveUserCap);
        }
        setBootstrappedFields(missing);
      } catch (err) {
        setError(`The settings did not load. ${reasonOf(err)} Reload the page to try again.`);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /**
   * Required-non-negative-integer parser. Empty values throw — every field
   * on this page MUST have a value (no implicit fallback to a code default).
   * 0 is valid for cap fields ("0 allowed", a way to fully disable a quota
   * target). Floors that can't sensibly be 0 should use a more restrictive
   * parser at the call site. `fieldName` reads after "the": "limit on ...".
   */
  const parseNonNegativeInt = (raw: string, fieldName: string): number => {
    if (raw === '') {
      throw new Error(`Set the ${fieldName}.`);
    }
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || !Number.isInteger(n) || n < 0) {
      throw new Error(`The ${fieldName} must be a whole number, 0 or more.`);
    }
    return n;
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    let payload: PlatformSettingsType;
    try {
      if (schedulingMinInterval === '') {
        throw new Error('Set the shortest time between runs.');
      }
      const parsedInterval = parseInt(schedulingMinInterval, 10);
      if (isNaN(parsedInterval) || parsedInterval < PLATFORM_DEFAULT_MIN || parsedInterval > PLATFORM_MAX_MIN) {
        throw new Error(
          `The shortest time between runs must be a whole number from ${PLATFORM_DEFAULT_MIN} to ${PLATFORM_MAX_MIN} minutes.`
        );
      }

      payload = {
        schedulingMinIntervalMinutes: parsedInterval,
        maxRunsPerCompanyPerMonth: parseNonNegativeInt(
          maxRunsPerCompanyPerMonth,
          'limit on scheduled runs for each company'
        ),
        maxRunsPerUserPerMonth: parseNonNegativeInt(maxRunsPerUserPerMonth, 'limit on scheduled runs for each person'),
        maxTriggerRunsPerCompanyPerMonth: parseNonNegativeInt(
          maxTriggerRunsPerCompanyPerMonth,
          'limit on triggered runs for each company'
        ),
        maxTriggerRunsPerUserPerMonth: parseNonNegativeInt(
          maxTriggerRunsPerUserPerMonth,
          'limit on triggered runs for each person'
        ),
        maxConcurrentActiveSchedulesPerCompany: parseNonNegativeInt(
          maxConcurrentActiveSchedulesPerCompany,
          'limit on automations switched on for each company'
        ),
        maxConcurrentActiveSchedulesPerUser: parseNonNegativeInt(
          maxConcurrentActiveSchedulesPerUser,
          'limit on automations switched on for each person'
        ),
        requireApprovalAboveUserCap,
      };
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return;
    }

    try {
      setSaving(true);
      await platformSettingsService.save(payload);
      setSuccess('Saved. Every client takes its limits from these settings.');
      setBootstrappedFields([]);
    } catch (err) {
      setError(`The settings were not saved. ${reasonOf(err)}`);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Cross-field consistency warnings. Each input falls back to the platform
   * default if left empty so we're validating what *will be* enforced — not
   * just what the user typed. Returned grouped by card so we can render the
   * banner alongside the relevant fields.
   */
  const warnings = useMemo(() => {
    const parse = (raw: string, fallback: number): number => {
      const n = parseInt(raw, 10);
      return Number.isFinite(n) && n > 0 ? n : fallback;
    };
    const minInt = parse(schedulingMinInterval, PLATFORM_DEFAULT_MIN);
    const sCompany = parse(maxRunsPerCompanyPerMonth, PLATFORM_QUOTA_INITIAL_VALUES.maxRunsPerCompanyPerMonth);
    const sUser = parse(maxRunsPerUserPerMonth, PLATFORM_QUOTA_INITIAL_VALUES.maxRunsPerUserPerMonth);
    const tCompany = parse(
      maxTriggerRunsPerCompanyPerMonth,
      PLATFORM_QUOTA_INITIAL_VALUES.maxTriggerRunsPerCompanyPerMonth
    );
    const tUser = parse(maxTriggerRunsPerUserPerMonth, PLATFORM_QUOTA_INITIAL_VALUES.maxTriggerRunsPerUserPerMonth);
    const cCompany = parse(
      maxConcurrentActiveSchedulesPerCompany,
      PLATFORM_QUOTA_INITIAL_VALUES.maxConcurrentActiveSchedulesPerCompany
    );
    const cUser = parse(
      maxConcurrentActiveSchedulesPerUser,
      PLATFORM_QUOTA_INITIAL_VALUES.maxConcurrentActiveSchedulesPerUser
    );
    const runsPerScheduleAtMinInterval = Math.floor(MINUTES_PER_MONTH / Math.max(minInt, 1));

    const schedules: string[] = [];
    if (sUser > sCompany) {
      schedules.push(
        `The limit for each person (${sUser.toLocaleString()}) is higher than the limit for each company (${sCompany.toLocaleString()}). A person can never run more than the company limit.`
      );
    }
    if (runsPerScheduleAtMinInterval > sUser) {
      schedules.push(
        `One schedule at the shortest time between runs makes about ${runsPerScheduleAtMinInterval.toLocaleString()} runs a month. That is more than the limit for each person (${sUser.toLocaleString()}), so nobody could run a schedule that often.`
      );
    }

    const triggers: string[] = [];
    if (tUser > tCompany) {
      triggers.push(
        `The limit for each person (${tUser.toLocaleString()}) is higher than the limit for each company (${tCompany.toLocaleString()}).`
      );
    }

    const concurrent: string[] = [];
    if (cUser > cCompany) {
      concurrent.push(
        `The limit for each person (${cUser.toLocaleString()}) is higher than the limit for each company (${cCompany.toLocaleString()}). One person could never have more automations on than the whole company may.`
      );
    }

    return { schedules, triggers, concurrent };
  }, [
    schedulingMinInterval,
    maxRunsPerCompanyPerMonth,
    maxRunsPerUserPerMonth,
    maxTriggerRunsPerCompanyPerMonth,
    maxTriggerRunsPerUserPerMonth,
    maxConcurrentActiveSchedulesPerCompany,
    maxConcurrentActiveSchedulesPerUser,
  ]);

  /** The limits in a card that do not fit together, shown above its fields. */
  const renderWarnings = (items: string[]): React.ReactNode => {
    if (items.length === 0) return null;
    return (
      <Alert variant="warning" className="platform-warning">
        <ExclamationTriangleFill className="platform-warning__icon" aria-hidden="true" />
        <div>
          <strong>These limits do not fit together</strong>
          <ul className="platform-warning__list">
            {items.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </div>
      </Alert>
    );
  };

  const neverSaved = bootstrappedFields.map(
    (field) => SETTING_NAME[field as keyof typeof PLATFORM_QUOTA_INITIAL_VALUES] ?? field
  );

  return (
    <ToolPage
      title="Platform settings"
      subtitle="Set the fleet-wide limits for automations: how many, how often, and when to warn."
      width="narrow"
    >
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <div className="platform-loading">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading the settings
        </div>
      ) : (
        <>
          {neverSaved.length > 0 && (
            <Alert variant="warning" className="platform-warning">
              <ExclamationTriangleFill className="platform-warning__icon" aria-hidden="true" />
              <div>
                <strong>
                  {neverSaved.length === 1
                    ? '1 setting was never saved'
                    : `${neverSaved.length} settings were never saved`}
                </strong>
                <div>
                  {neverSaved.join(', ')}. {neverSaved.length === 1 ? 'It is' : 'They are'} filled in below with the
                  recommended {neverSaved.length === 1 ? 'value' : 'values'}. Check and save: automations fail on every
                  client until every setting has a saved value.
                </div>
              </div>
            </Alert>
          )}

          <p className="platform-intro">
            Every setting needs a value. These are the limits every client starts from, and nothing in the code stands
            in for one that is missing.
          </p>

          <Card className="platform-card">
            <Card.Header>
              <h2 className="platform-card__title">How often</h2>
            </Card.Header>
            <Card.Body>
              <Form.Group controlId="platform-min-interval">
                <Form.Label>Shortest time between runs (minutes)</Form.Label>
                <Form.Control
                  type="number"
                  className="platform-number"
                  min={PLATFORM_DEFAULT_MIN}
                  max={PLATFORM_MAX_MIN}
                  step={1}
                  value={schedulingMinInterval}
                  onChange={(e) => setSchedulingMinInterval(e.target.value)}
                />
                <Form.Text>
                  No client can run an automation more often than this, unless its own config sets a shorter time. From{' '}
                  {PLATFORM_DEFAULT_MIN} to {PLATFORM_MAX_MIN.toLocaleString()} minutes.
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>

          <Card className="platform-card">
            <Card.Header>
              <h2 className="platform-card__title">Scheduled runs</h2>
              <p className="platform-card__about">
                The most runs a month that automations on a schedule may add up to. Numa works the number out from the
                schedule when the automation is made. A client can set a lower limit, never a higher one.
              </p>
            </Card.Header>
            <Card.Body>
              {renderWarnings(warnings.schedules)}
              <div className="platform-pair">
                <Form.Group controlId="platform-schedule-company">
                  <Form.Label>Each company, runs a month</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step={1}
                    value={maxRunsPerCompanyPerMonth}
                    onChange={(e) => setMaxRunsPerCompanyPerMonth(e.target.value)}
                  />
                  <Form.Text>
                    The ceiling for the whole workspace. An admin&apos;s approval cannot go past it.
                  </Form.Text>
                </Form.Group>
                <Form.Group controlId="platform-schedule-person">
                  <Form.Label>Each person, runs a month</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step={1}
                    value={maxRunsPerUserPerMonth}
                    onChange={(e) => setMaxRunsPerUserPerMonth(e.target.value)}
                  />
                  <Form.Text>Past this, the request goes to an admin when the switch below is on.</Form.Text>
                </Form.Group>
              </div>
              <Form.Group className="platform-switch">
                <Form.Check
                  type="switch"
                  id="require-approval-above-user-cap"
                  label="Ask an admin when a person's schedules would go past their limit"
                  checked={requireApprovalAboveUserCap}
                  onChange={(e) => setRequireApprovalAboveUserCap(e.target.checked)}
                />
                <Form.Text>
                  On: the request goes to an admin, who can allow up to the company limit and no more. Off: the request
                  is refused when the automation is made.
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>

          <Card className="platform-card">
            <Card.Header>
              <h2 className="platform-card__title">Triggered runs</h2>
              <p className="platform-card__about">
                The most runs a month for automations that an event starts, such as an email arriving. Numa counts each
                run as it happens. Past the limit a run is dropped, and the owner is told once that month.
              </p>
            </Card.Header>
            <Card.Body>
              {renderWarnings(warnings.triggers)}
              <div className="platform-pair">
                <Form.Group controlId="platform-trigger-company">
                  <Form.Label>Each company, runs a month</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step={1}
                    value={maxTriggerRunsPerCompanyPerMonth}
                    onChange={(e) => setMaxTriggerRunsPerCompanyPerMonth(e.target.value)}
                  />
                </Form.Group>
                <Form.Group controlId="platform-trigger-person">
                  <Form.Label>Each person, runs a month</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step={1}
                    value={maxTriggerRunsPerUserPerMonth}
                    onChange={(e) => setMaxTriggerRunsPerUserPerMonth(e.target.value)}
                  />
                </Form.Group>
              </div>
            </Card.Body>
          </Card>

          <Card className="platform-card">
            <Card.Header>
              <h2 className="platform-card__title">Automations switched on</h2>
              <p className="platform-card__about">
                How many automations, scheduled and triggered together, can be switched on at one time.
              </p>
            </Card.Header>
            <Card.Body>
              {renderWarnings(warnings.concurrent)}
              <div className="platform-pair">
                <Form.Group controlId="platform-active-company">
                  <Form.Label>Each company, automations</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step={1}
                    value={maxConcurrentActiveSchedulesPerCompany}
                    onChange={(e) => setMaxConcurrentActiveSchedulesPerCompany(e.target.value)}
                  />
                </Form.Group>
                <Form.Group controlId="platform-active-person">
                  <Form.Label>Each person, automations</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step={1}
                    value={maxConcurrentActiveSchedulesPerUser}
                    onChange={(e) => setMaxConcurrentActiveSchedulesPerUser(e.target.value)}
                  />
                </Form.Group>
              </div>
            </Card.Body>
          </Card>

          <div className="platform-save">
            <span className="platform-save__note">Saving changes these limits for every client.</span>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving
                </>
              ) : (
                'Save settings'
              )}
            </Button>
          </div>
        </>
      )}
    </ToolPage>
  );
}
